'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@kit/ui/utils';

import { ChannelPill, DestinationPanel, ProfileCard } from './nodes';
import { orthogonalPath } from './paths';
import { assignSlots, groupEdges } from './slots';
import { edgeToneClass } from './tones';
import type {
  ComputedEdge,
  EdgeTone,
  FlowLayout,
  Tone,
} from './types';

export type FlowChartProps = {
  layout: FlowLayout;
  tone: Tone;
  // The DestinationPanel can't know the product branding — this page is
  // about aXcelerate but the same engine is reused elsewhere. Provide a
  // render prop so consumers can plug in their own destination card.
  destination?: React.ReactNode;
};

export function FlowChart({ layout, tone, destination }: FlowChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [edgePaths, setEdgePaths] = useState<ComputedEdge[]>([]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const { outgoing, incoming } = groupEdges(layout.edges);

    function compute() {
      const rect = container.getBoundingClientRect();
      const next: ComputedEdge[] = [];
      // Track whether each edge is a back-edge so the label rotator
      // later knows which way to rotate (text flows UP along a back-
      // edge's vertical leg, DOWN along a forward edge's leg).
      const isBackByEdgeId = new Map<string, boolean>();

      const outSlots = assignSlots(container, layout, outgoing, 'out');
      const inSlots = assignSlots(container, layout, incoming, 'in');

      for (const edge of layout.edges) {
        const from = container.querySelector<HTMLElement>(
          `[data-flow-id="${edge.from}"]`,
        );
        const to = container.querySelector<HTMLElement>(
          `[data-flow-id="${edge.to}"]`,
        );
        if (!from || !to) continue;
        // Use the VISIBLE card (firstElementChild) for edge geometry, not
        // the wrapper grid cell. The wrapper spans the full 1fr lane
        // (often much wider than the card). Routing against the wrapper
        // makes sidewise edges exit/enter at the column boundary instead
        // of at the card edge — visible as a horizontal gap between the
        // arrow and the card it's supposed to be connected to.
        const fromCard = (from.firstElementChild ?? from) as HTMLElement;
        const toCard = (to.firstElementChild ?? to) as HTMLElement;
        const fr = fromCard.getBoundingClientRect();
        const tr = toCard.getBoundingClientRect();

        const fromFrac = outSlots.get(edge) ?? 0.5;
        const toFrac = inSlots.get(edge) ?? 0.5;

        const edgePad = 12;
        const arrowGap = 6;

        const forwardEndY = tr.top - rect.top - arrowGap;
        const forwardStartY = fr.bottom - rect.top;
        const isBackEdge = forwardEndY < forwardStartY;

        let d: string;
        let labelX: number;
        let labelY: number;
        let x1: number;
        let x2: number;
        let y1: number;
        let y2: number;
        // Only set for forward sidewise-L paths. Lets the label-
        // collision pass move a rotated label onto the vertical segment
        // instead of rotating it on top of the horizontal segment.
        let sidewiseVerticalX: number | undefined;
        let sidewiseVerticalMinY: number | undefined;
        let sidewiseVerticalMaxY: number | undefined;

        if (isBackEdge) {
          const outSlot = outSlots.get(edge) ?? 0.5;
          x1 =
            fr.left +
            edgePad +
            outSlot * (fr.width - edgePad * 2) -
            rect.left;
          y1 = fr.bottom - rect.top;
          // Back-edge enters the target's top using the same slot
          // formula as forward edges — so a single back-edge lands
          // dead-centre instead of biased to the left. If multiple
          // back-edges enter the same target, the incoming-slot
          // assigner distributes them.
          x2 =
            tr.left +
            edgePad +
            toFrac * (tr.width - edgePad * 2) -
            rect.left;
          y2 = tr.top - rect.top;

          // Stubs must be at least 2× the desired visible radius — the
          // orthogonalPath clamp is min(radius, inLen/2, outLen/2), so a
          // 40px stub with a 28px radius request clamps the tight corners
          // to ~17–20px (readable as a smooth curve, not a bevel).
          const stubDown = 40;
          const stubUp = 40;
          // Back-edge routing: pick the SHORTEST side to wrap. The lane
          // ideally sits in the gutter BETWEEN source and target so the
          // horizontal stub legs on each end are tiny. Falls back to
          // just-left-of-target when the cards horizontally overlap.
          const sourceFullyRight = fr.left > tr.right;
          const targetFullyRight = tr.left > fr.right;
          let backLaneX: number;
          if (sourceFullyRight) {
            // Source right of target — route through the shared gutter
            // (short path). The forward edge between the same pair is
            // routed as an orthogonal L-shape (see below) so the two
            // flows stay visually separated.
            backLaneX = (tr.right + fr.left) / 2 - rect.left;
          } else if (targetFullyRight) {
            backLaneX = (fr.right + tr.left) / 2 - rect.left;
          } else {
            backLaneX = Math.max(14, tr.left - rect.left - 10);
          }

          const waypoints: Array<[number, number]> = [
            [x1, y1],
            [x1, y1 + stubDown],
            [backLaneX, y1 + stubDown],
            [backLaneX, y2 - stubUp],
            [x2, y2 - stubUp],
            [x2, y2 - arrowGap],
          ];
          d = orthogonalPath(waypoints, 28);

          // Label on the UP segment (back-lane vertical) at its midpoint.
          // That x sits between the form lane and the AI lane — a clear
          // vertical gap no forward edge crosses. Midpoint y falls in the
          // row-gap between source and target, clear of every card.
          labelX = backLaneX;
          labelY = (y1 + stubDown + (y2 - stubUp)) / 2;
        } else {
          // Forward edge. If source and target are horizontally
          // SEPARATED cards (no horizontal overlap) AND in adjacent
          // rows, use a SIDEWISE port-based route — exit the right side
          // of source, run horizontally through the space between the
          // two rows, then drop into the target's top near its edge.
          // This keeps the forward edge out of the shared gutter so a
          // back-edge routed through the same gutter midpoint doesn't
          // overlap it.
          const sourceBelowTargetCol = fr.right <= tr.left; // source left of target
          const sourceAboveTargetCol = fr.left >= tr.right; // source right of target
          const adjacentRows = true; // always true for same-row+1 forwards
          const useSidewise =
            (sourceBelowTargetCol || sourceAboveTargetCol) && adjacentRows;

          if (useSidewise) {
            // Source exits from its RIGHT edge (if target is to the
            // right of source) or LEFT edge (if target is to the left).
            // Enter target at its TOP, near the corresponding corner.
            y1 = fr.top + fr.height * 0.6 - rect.top; // mid-lower of source
            const sourceExitX = sourceBelowTargetCol
              ? fr.right - rect.left
              : fr.left - rect.left;
            // Enter target top near the corner closest to source.
            const targetEntryX = sourceBelowTargetCol
              ? tr.left - rect.left + tr.width * 0.25
              : tr.left - rect.left + tr.width * 0.75;
            x1 = sourceExitX;
            x2 = targetEntryX;
            y2 = tr.top - rect.top - arrowGap;
            // Orthogonal L: exit horizontally, turn down at the target
            // column, descend into the target top.
            const waypoints: Array<[number, number]> = [
              [sourceExitX, y1],
              [targetEntryX, y1],
              [targetEntryX, y2],
            ];
            d = orthogonalPath(waypoints, 22);
            labelX = (sourceExitX + targetEntryX) / 2;
            labelY = y1;
            // Record the vertical segment so the collision pass can
            // shift a rotated label onto it — otherwise rotating the
            // label at the L's horizontal midpoint leaves vertical text
            // sitting over a horizontal segment.
            sidewiseVerticalX = targetEntryX;
            sidewiseVerticalMinY = y1;
            sidewiseVerticalMaxY = y2;
          } else {
            x1 =
              fr.left +
              edgePad +
              fromFrac * (fr.width - edgePad * 2) -
              rect.left;
            y1 = forwardStartY;
            y2 = tr.top - rect.top;

            const toWide = to.getAttribute('data-flow-wide') === 'true';
            const toLeft = tr.left - rect.left;
            const toRight = tr.right - rect.left;

            if (toWide) {
              x2 = Math.max(toLeft + 24, Math.min(x1, toRight - 24));
            } else {
              x2 =
                tr.left +
                edgePad +
                toFrac * (tr.width - edgePad * 2) -
                rect.left;
            }

            const endY = forwardEndY;

            const controlOffset = Math.max(
              24,
              Math.min(90, Math.abs(endY - y1) * 0.55),
            );
            d = `M ${x1} ${y1} C ${x1} ${y1 + controlOffset}, ${x2} ${endY - controlOffset}, ${x2} ${endY}`;
            labelX = (x1 + x2) / 2;
            labelY = (y1 + endY) / 2;

            y2 = endY;
          }
        }

        const AUTO_SOURCES = new Set(['axform', 'ai']);
        const edgeTone: EdgeTone =
          edge.tone ??
          (edge.to === 'ax'
            ? AUTO_SOURCES.has(edge.from)
              ? 'auto-enrolled'
              : 'manually-enrolled'
            : 'default');
        const label =
          edge.label ??
          (edgeTone === 'auto-enrolled'
            ? 'Auto enrolled'
            : edgeTone === 'manually-enrolled'
              ? 'Manually enrolled'
              : edgeTone === 'escalated'
                ? 'Escalated'
                : edgeTone === 'resolved'
                  ? 'Escalation resolved'
                  : undefined);

        const edgeId = `${edge.from}-${edge.to}`;
        isBackByEdgeId.set(edgeId, isBackEdge);
        next.push({
          id: edgeId,
          d,
          style: edge.style ?? 'solid',
          edgeTone,
          label,
          labelX,
          labelY,
          verticalSegX: sidewiseVerticalX,
          verticalSegMinY: sidewiseVerticalMinY,
          verticalSegMaxY: sidewiseVerticalMaxY,
        });
      }

      // ---- Label collision pass ----
      // Collect the VISIBLE card rect for every node — not the grid-cell
      // wrapper. The wrapper spans the full 1fr cell (often wider than
      // the card's visible max-width), and using it for collision detection
      // flags the gutter between cards as "inside a card" when it isn't.
      // Use the wrapper's first child (the actual ProfileCard / ChannelPill
      // / DestinationPanel) so we only block labels from crossing the
      // rendered card itself.
      const cardRects: Array<{
        left: number;
        top: number;
        right: number;
        bottom: number;
      }> = [];
      container.querySelectorAll('[data-flow-id]').forEach((el) => {
        const inner = (el.firstElementChild ?? el) as HTMLElement;
        const r = inner.getBoundingClientRect();
        cardRects.push({
          left: r.left - rect.left,
          top: r.top - rect.top,
          right: r.right - rect.left,
          bottom: r.bottom - rect.top,
        });
      });
      // Running list of label bboxes that have already been placed —
      // checked on each subsequent label so labels don't overlap each
      // other either.
      const placedLabels: Array<{
        left: number;
        top: number;
        right: number;
        bottom: number;
      }> = [];
      const overlapsRects = (
        ax: number,
        ay: number,
        bw: number,
        bh: number,
        against: Array<{ left: number; top: number; right: number; bottom: number }>,
      ): boolean => {
        const aRight = ax + bw;
        const aBottom = ay + bh;
        return against.some(
          (c) =>
            ax < c.right && aRight > c.left && ay < c.bottom && aBottom > c.top,
        );
      };
      const obstructs = (ax: number, ay: number, bw: number, bh: number) =>
        overlapsRects(ax, ay, bw, bh, cardRects) ||
        overlapsRects(ax, ay, bw, bh, placedLabels);
      const recordLabel = (ax: number, ay: number, bw: number, bh: number) => {
        placedLabels.push({
          left: ax,
          top: ay,
          right: ax + bw,
          bottom: ay + bh,
        });
      };
      const finalEdges: ComputedEdge[] = [];
      for (const p of next) {
        if (!p.label) {
          finalEdges.push(p);
          continue;
        }
        const words = p.label.split(' ');
        const longest = words.reduce(
          (acc, w) => (w.length > acc ? w.length : acc),
          0,
        );
        const pad = 2;
        const hW = longest * 4.8 + 10;
        const hH = 6 + words.length * 9;
        const hx = p.labelX - hW / 2 - pad;
        const hy = p.labelY - hH / 2 - pad;
        const hCollides = obstructs(hx, hy, hW + pad * 2, hH + pad * 2);
        if (!hCollides) {
          recordLabel(hx, hy, hW + pad * 2, hH + pad * 2);
          finalEdges.push(p);
          continue;
        }
        const singleLen = p.label.length;
        const rW = singleLen * 4.8 + 10;
        const rH = 6 + 9;
        const isBack = !!isBackByEdgeId.get(p.id);
        // A rotated (vertical) label must sit on a VERTICAL segment of
        // the path — otherwise the text reads 90° off from the line
        // it's annotating. For forward sidewise-L paths the vertical
        // segment is at verticalSegX; shift the rotated label onto it.
        // Back-edges already place labelX on the back-lane (vertical)
        // so no shift is needed. Forward non-sidewise curves don't
        // have a clean vertical segment — rotating in place is the
        // least-bad option.
        const rotatedLabelX =
          !isBack && p.verticalSegX != null ? p.verticalSegX : p.labelX;
        // Walk the rotated label along the vertical range. For sidewise
        // edges, start at the segment's midpoint and walk outward.
        const rotatedLabelBaseY =
          !isBack && p.verticalSegMinY != null && p.verticalSegMaxY != null
            ? (p.verticalSegMinY + p.verticalSegMaxY) / 2
            : p.labelY;
        const offsetCandidates = [0, -20, 20, -40, 40, -60, 60];
        let placed = false;
        for (const dy of offsetCandidates) {
          const tryY = rotatedLabelBaseY + dy;
          // Clamp to the vertical segment's Y range so the label stays
          // on the line it's meant to annotate.
          if (
            !isBack &&
            p.verticalSegMinY != null &&
            p.verticalSegMaxY != null
          ) {
            const rotatedHalf = rW / 2;
            if (
              tryY - rotatedHalf < p.verticalSegMinY ||
              tryY + rotatedHalf > p.verticalSegMaxY
            ) {
              continue;
            }
          }
          // Rotated label is ~rW tall × rH wide, centred at
          // (rotatedLabelX, tryY).
          const trx = rotatedLabelX - rH / 2 - pad;
          const try_ = tryY - rW / 2 - pad;
          if (obstructs(trx, try_, rH + pad * 2, rW + pad * 2)) continue;
          const rotation: 90 | -90 = isBack ? -90 : 90;
          recordLabel(trx, try_, rH + pad * 2, rW + pad * 2);
          finalEdges.push({
            ...p,
            labelX: rotatedLabelX,
            labelY: tryY,
            labelRotation: rotation,
          });
          placed = true;
          break;
        }
        if (placed) continue;
        recordLabel(hx, hy, hW + pad * 2, hH + pad * 2);
        finalEdges.push(p);
      }
      setEdgePaths(finalEdges);
    }

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [layout]);

  // When the chart's container stretches (parent uses flex-col with
  // h-full), an inserted `1fr` spacer row before the destination
  // pushes the destination node down to the bottom. That makes paired
  // Traditional/Leagen charts in a 2-col grid end their aXcelerate
  // destination at the same y position regardless of how many rows of
  // handlers each side has. Without a destination node, we fall back
  // to all-auto rows (no stretching).
  const destRow = layout.nodes.find((n) => n.kind === 'destination')?.row;
  const gridTemplateRows = destRow
    ? `repeat(${destRow - 1}, auto) 1fr auto`
    : undefined;
  return (
    <div ref={containerRef} className="relative flex h-full flex-col">
      <div
        className="grid flex-1 gap-x-8 gap-y-10"
        style={{
          gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
          ...(gridTemplateRows ? { gridTemplateRows } : {}),
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {layout.nodes.map((node) => {
            const justifyClass =
              node.justify === 'start'
                ? 'justify-start'
                : node.justify === 'end'
                  ? 'justify-end'
                  : 'justify-center';
            const isDestination = node.kind === 'destination';
            return (
              <motion.div
                key={node.id}
                data-flow-id={node.id}
                data-flow-wide={(node.colSpan ?? 1) > 1 ? 'true' : 'false'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className={cn(
                  'relative flex',
                  !isDestination && 'h-full',
                  justifyClass,
                )}
                style={{
                  gridColumn: `${node.col} / span ${node.colSpan ?? 1}`,
                  // Destination node lives in the auto-height row AFTER
                  // the 1fr spacer, so it keeps its natural height
                  // while the spacer above eats any extra column
                  // height. Without this, the destination's cell lands
                  // on the 1fr row and gets stretched vertically.
                  gridRow: isDestination ? node.row + 1 : node.row,
                  marginTop: isDestination ? 110 : undefined,
                }}
              >
                {node.kind === 'channel' ? (
                  <ChannelPill
                    icon={node.icon}
                    customIcon={node.customIcon}
                    label={node.label}
                    volume={node.volume}
                  />
                ) : node.kind === 'destination' ? (
                  destination ?? <DestinationPanel fallbackLabel={node.label} />
                ) : (
                  <ProfileCard node={node} colSpan={node.colSpan ?? 1} />
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      <svg className="pointer-events-none absolute inset-0 h-full w-full">
        <defs>
          {(
            [
              'default',
              'auto-enrolled',
              'manually-enrolled',
              'escalated',
              'resolved',
            ] as EdgeTone[]
          ).map((t) => {
            const colorClass = edgeToneClass(t);
            return (
              <marker
                key={t}
                id={`flow-arrow-${tone}-${t}`}
                markerWidth={6}
                markerHeight={6}
                refX={5}
                refY={3}
                orient="auto"
                markerUnits="strokeWidth"
                className={colorClass}
              >
                <path d="M0,0 L6,3 L0,6 Z" fill="currentColor" />
              </marker>
            );
          })}
        </defs>
        {/* All edge PATHS first, so any path crossing a label's
            position lands beneath the label's opaque pill. */}
        <AnimatePresence mode="popLayout" initial={false}>
          {edgePaths.map((p) => {
            const colorClass = edgeToneClass(p.edgeTone);
            return (
              <motion.g
                key={`path-${p.id}`}
                className={colorClass}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                <path
                  d={p.d}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.25}
                  strokeDasharray={p.style === 'dashed' ? '4 4' : undefined}
                  markerEnd={`url(#flow-arrow-${tone}-${p.edgeTone})`}
                />
              </motion.g>
            );
          })}
        </AnimatePresence>
        {/* All LABELS second, painted on top of every edge path. */}
        <AnimatePresence mode="popLayout" initial={false}>
          {edgePaths.map((p) => {
            if (!p.label) return null;
            const colorClass = edgeToneClass(p.edgeTone);
            const rotation = p.labelRotation ?? 0;
            // When rotated, render the label as a SINGLE line of text
            // (words joined with a space) so the text flows along the
            // line direction instead of being broken into stacked
            // words. When horizontal, keep the multi-line layout.
            const singleLine = rotation !== 0;
            const words = singleLine ? [p.label] : p.label.split(' ');
            const lineHeight = 9;
            const longest = words.reduce(
              (acc, w) => (w.length > acc ? w.length : acc),
              0,
            );
            const labelW = longest * 4.8 + 10;
            const labelH = 6 + words.length * lineHeight;
            const topY = p.labelY - labelH / 2;
            const body = (
              <g>
                <rect
                  x={p.labelX - labelW / 2}
                  y={topY}
                  width={labelW}
                  height={labelH}
                  rx={6}
                  className="fill-background stroke-current"
                  strokeWidth={1}
                  fillOpacity={1}
                />
                <text
                  x={p.labelX}
                  textAnchor="middle"
                  className="fill-current font-semibold tracking-wide"
                  fontSize={7.5}
                >
                  {words.map((w, i) => (
                    <tspan
                      key={i}
                      x={p.labelX}
                      y={topY + 4 + (i + 0.5) * lineHeight}
                      dominantBaseline="central"
                    >
                      {w}
                    </tspan>
                  ))}
                </text>
              </g>
            );
            return (
              <motion.g
                key={`label-${p.id}`}
                className={colorClass}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                transform={
                  rotation !== 0
                    ? `rotate(${rotation} ${p.labelX} ${p.labelY})`
                    : undefined
                }
              >
                {body}
              </motion.g>
            );
          })}
        </AnimatePresence>
      </svg>
    </div>
  );
}
